import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser, faFileContract, faCalendarAlt, faHandshake, faMoneyBillWave, faFileAlt, faFileInvoice, faBox, faSearch, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import { fetchHMSEntities } from '../../services/hmsEntityService';
import { crmService } from '../../services/crmService';
import axiosInstance from '../../utils/axiosConfig';
import { dashboardService } from '../../services/dashboardService';
import { useUserInfo } from '../../contexts/UserContext';
import { resolveIssuerName } from '../../utils/issuerDisplay';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import InvoiceValidationModal, { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import { buildDeliveryInternalReference } from '../../utils/deliveryReferenceBuilder';
import PledgeBankField, { dealPledgeNote, pledgeFieldsFor, pledgeOf } from '../../components/shared/PledgeBankField';
import './OutgoingInvoice.css';

/**
 * Screen 1: Header & Partner Information
 * 
 * Fields:
 * - HMS Entity (dropdown or from delivery)
 * - Issuer (automatic - logged in user)
 * - Invoice Template Type (dropdown from template types)
 * - Pro-Forma Invoice Number (auto-generate)
 * - Invoice Date (calendar)
 * - Internal Reference (delivery number, contract numbers, sales recap - auto)
 * - Partner Name (from CRM or Delivery or pro-forma, option to edit)
 * - Due Date (calculated from payment terms + choice of delivery/B/L date)
 * - Payment Terms (dropdown or from CRM/delivery/pro forma)
 * - Customer Notes (text field, stored as supply_reference; row 158)
 */

const ProformaInvoiceScreen1 = ({ 
  invoiceData, 
  setInvoiceData, 
  vesselInfo: propVesselInfo = null,
  setVesselInfo: propSetVesselInfo = null,
  onNext, 
  sourceDelivery = null,
  sourceProforma = null,
  isReadOnly = false,
  issuerName = null,
  isPersisted = false
}) => {
  const [loading, setLoading] = useState(true);
  const [hmsEntities, setHmsEntities] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [dueDateBasis, setDueDateBasis] = useState('DELIVERY_DATE');
  const [vesselInfo, setVesselInfo] = useState(propVesselInfo);
  const [isManualDueDateEdit, setIsManualDueDateEdit] = useState(false);

  // Fuels invoices use the 'fuels_eng' template (auto-stamped by Sprint 4C
  // delivery_mapper for fuels deliveries). Drives the BL -> BDN label flip
  // on the Due Date Basis radio. Solids label "from B/L date" stays as-is.
  const blDateBasisLabel = invoiceData?.template_type === 'fuels_eng'
    ? 'from BDN date'
    : 'from B/L date';
  
  // Update local vesselInfo when prop changes (from parent CreateProformaInvoice)
  useEffect(() => {
    if (propVesselInfo) {
      setVesselInfo(propVesselInfo);
    }
  }, [propVesselInfo]);
  
  // Helper function to parse internal reference into separate parts
  const parseInternalReference = (internalRef) => {
    if (!internalRef) return { delivery: '', contracts: '', salesRecaps: '' };
    
    const lines = internalRef.split('\n').map(line => line.trim()).filter(line => line);
    let delivery = '';
    let contracts = [];
    let salesRecaps = '';
    
    lines.forEach(line => {
      if (line.startsWith('Delivery:')) {
        delivery = line.replace('Delivery:', '').trim();
      } else if (line.includes('Contract:')) {
        contracts.push(line.replace('Seller Contract:', '').replace('Buyer Contract:', '').trim());
      } else if (line.startsWith('Sales Recaps:')) {
        salesRecaps = line.replace('Sales Recaps:', '').trim();
      }
    });
    
    return {
      delivery,
      contracts: contracts.join(', '),
      salesRecaps
    };
  };

  // Rebuild internal_reference from the three editable sub-fields while
  // preserving any other lines (e.g. the "From Pro-forma:" trace). Uses
  // "Seller Contract:" so the value round-trips through parseInternalReference.
  const updateInternalReference = (field, value) => {
    setInvoiceData(prev => {
      const current = parseInternalReference(prev.internal_reference);
      current[field] = value;
      const extras = (prev.internal_reference || '')
        .split('\n').map(l => l.trim()).filter(Boolean)
        .filter(l => !l.startsWith('Delivery:') && !l.includes('Contract:') && !l.startsWith('Sales Recaps:') && !l.startsWith('Purchase Recaps:'));
      const lines = [];
      if (current.delivery) lines.push(`Delivery: ${current.delivery}`);
      if (current.contracts) lines.push(`Seller Contract: ${current.contracts}`);
      if (current.salesRecaps) lines.push(`Sales Recaps: ${current.salesRecaps}`);
      return { ...prev, internal_reference: [...lines, ...extras].join('\n') };
    });
  };
  
  // Partner search with debouncing
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
  const [debouncedPartnerSearch, setDebouncedPartnerSearch] = useState('');
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [partnerSearchLoading, setPartnerSearchLoading] = useState(false);
  const [selectedPartnerAddress, setSelectedPartnerAddress] = useState('');
  const partnerSearchRef = useRef(null);
  
  // Get current user info
  const { userInfo, updateUserInfo } = useUserInfo();
  const [sessionUserName, setSessionUserName] = useState('');
  
  useEffect(() => {
    loadDropdownData();
    loadIssuerInfo();
    loadVesselInfo();
  }, []);
  
  const loadIssuerInfo = async () => {
    try {
      // Try from context first
      if (userInfo.first_name && userInfo.last_name) {
        setSessionUserName(`${userInfo.first_name} ${userInfo.last_name}`.trim());
      } else {
        // Fetch from API if context is empty (page reload)
        const userInfoData = await dashboardService.getUserInfo();
        updateUserInfo(userInfoData);
        setSessionUserName(`${userInfoData.first_name} ${userInfoData.last_name}`.trim());
      }
    } catch (error) {
      console.error('Error loading issuer info:', error);
      setSessionUserName('Automatic (person creating the invoice)');
    }
  };

  // The Issuer names whoever RAISED this document, which is not whoever is
  // looking at it -- only an unsaved document belongs to the person typing.
  const issuerFullName = resolveIssuerName({
    isPersisted,
    storedIssuerName: issuerName,
    sessionUserName,
  });
  
  useEffect(() => {
    // Initialize partner search term from saved data
    if (invoiceData.partner_name && !partnerSearchTerm) {
      setPartnerSearchTerm(invoiceData.partner_name);
    }
  }, [invoiceData.partner_name]);
  
  useEffect(() => {
    // Auto-calculate due date when payment terms, basis, or vessel info changes
    // Skip if CUSTOM payment term is selected or if it's a manual edit
    const customTerm = paymentTerms.find(pt => pt.term_code === 'CUSTOM');
    if (invoiceData.payment_terms && 
        invoiceData.payment_terms !== customTerm?.id && 
        !isManualDueDateEdit) {
      calculateDueDate();
    }
  }, [invoiceData.payment_terms, invoiceData.invoice_date, dueDateBasis, vesselInfo, paymentTerms]);
  
  
  // Debounce partner search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPartnerSearch(partnerSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [partnerSearchTerm]);
  
  // Fetch partners when debounced search changes
  useEffect(() => {
    if (debouncedPartnerSearch.trim().length >= 2) {
      fetchApprovedPartners(debouncedPartnerSearch);
    } else {
      setFilteredPartners([]);
    }
  }, [debouncedPartnerSearch]);
  
  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (partnerSearchRef.current && !partnerSearchRef.current.contains(event.target)) {
        setShowPartnerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const fetchApprovedPartners = async (searchTerm) => {
    try {
      setPartnerSearchLoading(true);
      
      // Use the SAME method as CRM screens
      const results = await crmService.searchProfilesByName(searchTerm);
      
      // Filter for APPROVED profiles only (client-side since API doesn't support it)
      const approvedProfiles = results.filter(profile => 
        (profile.approval_status || '').toString().toLowerCase() === 'approved' || profile.approval_status === undefined
      );
      
      const partnersData = approvedProfiles.map(profile => ({
        id: profile.id,
        legal_entity_name: profile.legal_entity_name,
        trading_short_name: profile.trading_short_name,
        base_currency: profile.base_currency || 'USD',
        head_office_address: profile.head_office_address || '',
        display_name: profile.trading_short_name
          ? `${profile.legal_entity_name} (${profile.trading_short_name})`
          : profile.legal_entity_name
      }));
      
      setFilteredPartners(partnersData);
      setPartnerSearchLoading(false);
    } catch (error) {
      console.error('Error fetching approved partners:', error);
      setFilteredPartners([]);
      setPartnerSearchLoading(false);
    }
  };
  
  const loadDropdownData = async () => {
    try {
      setLoading(true);
      
      // Fetch all required dropdown data
      const [entitiesData, dropdownsData] = await Promise.all([
        fetchHMSEntities(),
        invoicingService.fetchInvoicingDropdowns()
      ]);
      
      setHmsEntities(entitiesData);
      setPaymentTerms(dropdownsData.payment_terms || []);
      setTemplateTypes(dropdownsData.template_types || []);
      
      // If creating from delivery, pre-fill data
      if (sourceDelivery) {
        prefillFromDelivery(sourceDelivery);
      }
      
      // If creating from proforma, pre-fill data
      if (sourceProforma) {
        prefillFromProforma(sourceProforma);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      setLoading(false);
    }
  };
  
  const prefillFromDelivery = async (delivery) => {
    setInvoiceData(prev => ({
      ...prev,
      hms_entity: delivery.hms_entity,
      internal_reference: buildDeliveryInternalReference(delivery, 'sale'),
      currency: delivery.sale_price_per_ton_contract_currency || 'USD',
    }));
    
    // Search for partner by name from delivery
    if (delivery.delivery_buyer_crm_partner_name) {
      setPartnerSearchTerm(delivery.delivery_buyer_crm_partner_name);
      try {
        const response = await crmService.getProfiles({
          search: delivery.delivery_buyer_crm_partner_name,
          approval_status: 'APPROVED'
        });
        const partners = response.results || response || [];
        if (partners.length > 0) {
          const matchedPartner = partners[0];
          setInvoiceData(prev => ({
            ...prev,
            partner: matchedPartner.id,
            partner_name: matchedPartner.legal_entity_name
          }));
        }
      } catch (error) {
        console.error('Error pre-filling partner from delivery:', error);
      }
    }
  };
  
  const prefillFromProforma = (proforma) => {
    setInvoiceData(prev => ({
      ...prev,
      ...proforma,
      internal_reference: (proforma.internal_reference || '') + `\nFrom Pro-forma: ${proforma.invoice_number}`,
    }));
    
    // Pre-fill partner search term if partner already selected
    if (proforma.partner_name) {
      setPartnerSearchTerm(proforma.partner_name);
    }
  };
  
  const handleSelectPartner = (partner) => {
    setInvoiceData(prev => ({
      ...prev,
      partner: partner.id,
      partner_name: partner.legal_entity_name,
      currency: partner.base_currency || prev.currency || 'USD'
    }));
    setPartnerSearchTerm(partner.display_name);
    setSelectedPartnerAddress(partner.head_office_address || '');
    setShowPartnerDropdown(false);
  };
  
  const loadVesselInfo = async () => {
    // Load vessel info if invoice already exists (for edit mode)
    if (invoiceData.id) {
      try {
        const response = await axiosInstance.get(`/api/invoicing/proforma-invoices/${invoiceData.id}/vessel-info/`);
        if (response.data && response.data.length > 0) {
          setVesselInfo(response.data[0]);
        }
      } catch (error) {
        console.error('Error loading vessel info:', error);
      }
    }
  };

  const calculateDueDate = () => {
    const paymentTerm = paymentTerms.find(pt => pt.id === invoiceData.payment_terms);
    if (!paymentTerm) return;
    
    let baseDate;
    
    // Determine base date based on selected basis
    if (dueDateBasis === 'DELIVERY_DATE' && vesselInfo?.delivery_date) {
      baseDate = new Date(vesselInfo.delivery_date);
    } else if (dueDateBasis === 'BL_DATE' && vesselInfo?.bl_date) {
      baseDate = new Date(vesselInfo.bl_date);
    } else if (invoiceData.invoice_date) {
      // Fall back to invoice date if selected date not available
      baseDate = new Date(invoiceData.invoice_date);
    } else {
      return; // No valid base date available
    }
    
    // Clause terms (LC, CAD) carry wording, not a day count, so
    // days_until_due is null. JS would coerce that to 0 and silently stamp
    // the invoice due on the base date -- leave the field for the user.
    const daysToAdd = paymentTerm.days_until_due;
    if (daysToAdd === null || daysToAdd === undefined) return;
    
    // Calculate due date: base_date + payment_terms_days
    const dueDate = new Date(baseDate);
    dueDate.setDate(dueDate.getDate() + daysToAdd);
    
    setIsManualDueDateEdit(false); // Reset manual edit flag
    setInvoiceData(prev => ({ 
      ...prev, 
      due_date: dueDate.toISOString().split('T')[0],
      due_date_basis: dueDateBasis
    }));
  };

  const handleManualDueDateChange = (newDate) => {
    // User manually selected a due date - switch to CUSTOM payment term
    const customTerm = paymentTerms.find(pt => pt.term_code === 'CUSTOM');
    
    if (customTerm) {
      setIsManualDueDateEdit(true);
      setInvoiceData(prev => ({ 
        ...prev, 
        due_date: newDate,
        payment_terms: customTerm.id,
        due_date_basis: 'MANUAL'
      }));
    } else {
      // Fallback if CUSTOM term doesn't exist
      setInvoiceData(prev => ({ 
        ...prev, 
        due_date: newDate,
        due_date_basis: 'MANUAL'
      }));
    }
  };
  
  const handleNext = () => {
    onNext();
  };
  
  if (loading) {
    return (
      <div className="invoice-loading">
        <div className="loading-spinner"></div>
        <p>Loading invoice data...</p>
      </div>
    );
  }
  
  return (
    <>
      <div className="invoice-screen-content">
        {/* Delivery Pre-fill Indicator */}
        {invoiceData._prefilled_from_delivery && (
          <div className="prefill-indicator" style={{
            backgroundColor: '#2196F3',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <FontAwesomeIcon icon={faFileContract} />
            <span style={{ fontWeight: '500' }}>
              Data pre-filled from Delivery {invoiceData._prefilled_delivery_number || `#${invoiceData._prefilled_from_delivery}`}
            </span>
          </div>
        )}
        
        <div className="invoice-form-section">
        <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <FontAwesomeIcon icon={faBuilding} /> Invoice Header & Partner Information
          </span>
          <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
            {invoiceData.invoice_number ? `Pro-Forma Invoice: ${invoiceData.invoice_number}` : '(number will be generated on save)'}
          </span>
        </h2>
        
        <div className="invoice-form-grid">
          {/* Left Column */}
          <div className="invoice-form-column">
            {/* IMT Entity */}
            <div className="form-field">
              <label className="field-label required">
                <FontAwesomeIcon icon={faBuilding} /> Entity
              </label>
              {isReadOnly ? (
                <div className="form-display-value">
                  {hmsEntities.find(e => e.value === invoiceData.hms_entity)?.label || 'N/A'}
                </div>
              ) : (
                <select
                  id="proforma_hms_entity_select"
                  className="form-select"
                  value={invoiceData.hms_entity || ''}
                  onChange={(e) => {
                    setInvoiceData(prev => ({ ...prev, hms_entity: parseInt(e.target.value) }));
                    clearFieldError('proforma_hms_entity_select');
                  }}
                  disabled={sourceDelivery || sourceProforma}
                >
                  <option value="">Select Entity...</option>
                  {hmsEntities.map(entity => (
                    <option key={entity.value} value={entity.value}>
                      {entity.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {/* Issuer */}
            <div className="form-field">
              <label className="field-label required">
                <FontAwesomeIcon icon={faUser} /> Issuer
              </label>
              <input
                type="text"
                className="form-input"
                value={issuerFullName}
                readOnly
                disabled
              />
            </div>
            
            {/* Invoice Template Type */}
            <div className="form-field">
              <label className="field-label required">
                <FontAwesomeIcon icon={faFileInvoice} /> Invoice Template Type
              </label>
              {isReadOnly ? (
                <div className="form-display-value">
                  {templateTypes.find(t => t.value === invoiceData.template_type)?.label || 'N/A'}
                </div>
              ) : (
                <select
                  id="template_type_select"
                  className="form-select"
                  value={invoiceData.template_type || ''}
                  onChange={(e) => {
                    setInvoiceData(prev => ({ ...prev, template_type: e.target.value }));
                    clearFieldError('template_type_select');
                  }}
                >
                  <option value="">Select Template Type...</option>
                  {templateTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {/* Pro-Forma Invoice Number */}
            <div className="form-field">
              <label className="field-label required">
                <FontAwesomeIcon icon={faFileInvoice} /> Pro-Forma Invoice Number
              </label>
              <input
                type="text"
                className="form-input"
                value={invoiceData.invoice_number || "(will be generated on save)"}
                readOnly
                disabled
              />
            </div>
            
            {/* Invoice Date */}
            <div className="form-field">
              <InvoiceDatePicker
                value={invoiceData.invoice_date || ''}
                onChange={(e) => {
                  setInvoiceData(prev => ({ ...prev, invoice_date: e.target.value }));
                  clearFieldError('invoice_date');
                }}
                name="invoice_date"
                id="invoice_date"
                label={
                  <span>
                    <FontAwesomeIcon icon={faCalendarAlt} /> Invoice Date
                  </span>
                }
                placeholder="Select invoice date"
                required={true}
                className="form-input"
                readOnly={isReadOnly}
              />
            </div>
            
            {/* Internal Reference */}
            <div className="form-field">
              <label className="field-label">
                <FontAwesomeIcon icon={faFileAlt} /> Internal Reference
              </label>
              <div className="internal-reference-group">
                <input
                  type="text"
                  className="form-input"
                  placeholder="delivery number (auto)"
                  value={parseInternalReference(invoiceData.internal_reference).delivery}
                  onChange={(e) => updateInternalReference('delivery', e.target.value)}
                  readOnly={isReadOnly}
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="contract numbers (auto)"
                  value={parseInternalReference(invoiceData.internal_reference).contracts}
                  onChange={(e) => updateInternalReference('contracts', e.target.value)}
                  readOnly={isReadOnly}
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="sales recap numbers (auto)"
                  value={parseInternalReference(invoiceData.internal_reference).salesRecaps}
                  onChange={(e) => updateInternalReference('salesRecaps', e.target.value)}
                  readOnly={isReadOnly}
                />
              </div>
            </div>
          </div>
          
          {/* Right Column */}
          <div className="invoice-form-column">
            {/* Partner Name - Searchable CRM Profile Dropdown */}
            <div className="form-field" ref={partnerSearchRef}>
              <label className="field-label required">
                <FontAwesomeIcon icon={faHandshake} /> Partner (CRM Profile)
              </label>
              {isReadOnly ? (
                <div className="form-display-value">
                  {invoiceData.partner_name || 'N/A'}
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <FontAwesomeIcon 
                      icon={faSearch} 
                      style={{ 
                        position: 'absolute', 
                        left: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        color: '#666',
                        pointerEvents: 'none'
                      }} 
                    />
                    <input
                      type="text"
                      id="proforma_partner_search_input"
                      className="form-input"
                      style={{ paddingLeft: '36px' }}
                      value={partnerSearchTerm}
                      onChange={(e) => {
                        setPartnerSearchTerm(e.target.value);
                        setSelectedPartnerAddress('');
                        setShowPartnerDropdown(true);
                        clearFieldError('proforma_partner_search_input');
                      }}
                      onFocus={() => setShowPartnerDropdown(true)}
                      placeholder="Search APPROVED CRM profiles by Legal Entity Name..."
                      disabled={sourceDelivery || sourceProforma}
                    />
                  </div>
                  
                  {showPartnerDropdown && debouncedPartnerSearch.trim().length >= 2 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    maxHeight: '250px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {partnerSearchLoading ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        Searching...
                      </div>
                    ) : filteredPartners.length > 0 ? (
                      filteredPartners.map(partner => (
                        <div
                          key={partner.id}
                          onClick={() => handleSelectPartner(partner)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div style={{ fontWeight: '600', color: '#4a6b25' }}>
                            {partner.legal_entity_name}
                          </div>
                          {partner.trading_short_name && (
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                              Trading as: {partner.trading_short_name}
                            </div>
                          )}
                          {partner.head_office_address && (
                            <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <FontAwesomeIcon icon={faMapMarkerAlt} /> {partner.head_office_address}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                        No APPROVED partners found for "{debouncedPartnerSearch}"
                      </div>
                      )}
                    </div>
                  )}
                  {invoiceData.partner && selectedPartnerAddress && (
                    <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedPartnerAddress}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Payment Terms */}
            <div className="form-field">
              <label className="field-label required">
                <FontAwesomeIcon icon={faMoneyBillWave} /> Payment Terms
              </label>
              {isReadOnly ? (
                <div className="form-display-value">
                  {paymentTerms.find(t => t.id === invoiceData.payment_terms)?.term_code || 'N/A'} - {paymentTerms.find(t => t.id === invoiceData.payment_terms)?.description || ''}
                </div>
              ) : (
                <select
                  id="proforma_payment_terms_select"
                  className="form-select"
                  value={invoiceData.payment_terms || ''}
                  onChange={(e) => {
                    setInvoiceData(prev => ({ ...prev, payment_terms: parseInt(e.target.value) }));
                    clearFieldError('proforma_payment_terms_select');
                  }}
                >
                  <option value="">Select Payment Terms...</option>
                  {paymentTerms.map(term => (
                    <option key={term.id} value={term.id}>
                      {term.term_code} - {term.description}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {/* Pledge (go-live checklist), next to the payment terms. Ticked,
                the bank box on the last step offers only the pledge bank's
                accounts. A pledge inherited from the deal stays as the recap
                says; the server refuses any change here. */}
            <div className="form-field">
              <PledgeBankField
                idPrefix="proforma"
                entity={invoiceData.hms_entity}
                value={pledgeOf(invoiceData)}
                onChange={(next) => setInvoiceData(prev => ({ ...prev, ...pledgeFieldsFor(next) }))}
                locked={Boolean(invoiceData.pledge_from_deal)}
                lockedNote={dealPledgeNote(invoiceData)}
                note={invoiceData.pledge_from_deal ? '' : (invoiceData.pledge_note || '')}
                readOnly={isReadOnly}
              />
            </div>

            {/* Due Date Basis Selection - Hide if CUSTOM payment term selected */}
            {!isReadOnly && (() => {
              const customTerm = paymentTerms.find(pt => pt.term_code === 'CUSTOM');
              const isCustomSelected = customTerm && invoiceData.payment_terms === customTerm.id;
              return !isCustomSelected && (
                <div className="form-field">
                  <label className="field-label">
                    <FontAwesomeIcon icon={faCalendarAlt} /> Due Date Calculation Basis
                  </label>
                  <div className="due-date-choices">
                    <div className="due-date-checkbox-item">
                      <input
                        type="checkbox"
                        id="proforma-delivery-date-checkbox"
                        className="due-date-checkbox"
                        checked={dueDateBasis === 'DELIVERY_DATE'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDueDateBasis('DELIVERY_DATE');
                            setIsManualDueDateEdit(false);
                          }
                        }}
                      />
                      <label htmlFor="proforma-delivery-date-checkbox" className="due-date-checkbox-label">
                        from delivery date
                        {vesselInfo?.delivery_date && (
                          <span style={{ display: 'block', fontSize: '0.85em', color: '#999', fontStyle: 'italic', marginTop: '2px' }}>
                            {new Date(vesselInfo.delivery_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        {!vesselInfo?.delivery_date && (
                          <span style={{ display: 'block', fontSize: '0.85em', color: '#ccc', fontStyle: 'italic', marginTop: '2px' }}>
                            Not available
                          </span>
                        )}
                      </label>
                    </div>
                    <div className="due-date-checkbox-item">
                      <input
                        type="checkbox"
                        id="proforma-bl-date-checkbox"
                        className="due-date-checkbox"
                        checked={dueDateBasis === 'BL_DATE'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDueDateBasis('BL_DATE');
                            setIsManualDueDateEdit(false);
                          }
                        }}
                      />
                      <label htmlFor="proforma-bl-date-checkbox" className="due-date-checkbox-label">
                        {blDateBasisLabel}
                        {vesselInfo?.bl_date && (
                          <span style={{ display: 'block', fontSize: '0.85em', color: '#999', fontStyle: 'italic', marginTop: '2px' }}>
                            {new Date(vesselInfo.bl_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        {!vesselInfo?.bl_date && (
                          <span style={{ display: 'block', fontSize: '0.85em', color: '#ccc', fontStyle: 'italic', marginTop: '2px' }}>
                            Not available
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Due Date */}
            <div className="form-field">
              <label className="field-label required">
                <FontAwesomeIcon icon={faCalendarAlt} /> Due Date
              </label>
              <InvoiceDatePicker
                value={invoiceData.due_date || ''}
                onChange={(e) => handleManualDueDateChange(e.target.value)}
                name="due_date"
                id="due_date"
                placeholder="Select due date or calculated automatically"
                required={true}
                className="form-input"
                readOnly={isReadOnly}
              />
              {(() => {
                const customTerm = paymentTerms.find(pt => pt.term_code === 'CUSTOM');
                const isCustomSelected = customTerm && invoiceData.payment_terms === customTerm.id;
                return isCustomSelected && !isReadOnly && (
                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                    Manual date selected - Payment Terms set to "Custom Date"
                  </div>
                );
              })()}
            </div>
            
            {/* Customer Notes (stored as supply_reference) */}
            <div className="form-field-flexible">
              <label className="field-label">
                <FontAwesomeIcon icon={faFileAlt} /> Customer Notes
              </label>
              <textarea
                className="form-textarea"
                value={invoiceData.supply_reference || ''}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, supply_reference: e.target.value }))}
                placeholder="Enter customer notes..."
                readOnly={isReadOnly}
              />
              <label className="checkbox-label" style={{ marginTop: '6px' }}>
                <input
                  type="checkbox"
                  checked={invoiceData.supply_reference_visible_on_invoice || false}
                  onChange={(e) => setInvoiceData(prev => ({ ...prev, supply_reference_visible_on_invoice: e.target.checked }))}
                  disabled={isReadOnly}
                />
                <span>Show on the PDF as "Customer Notes" (under Customer No.)</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="screen-navigation" style={{ justifyContent: 'flex-end' }}>
          <button className="btn-primary" type="button" onClick={handleNext}>
            Next: Products <FontAwesomeIcon icon={faBox} />
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default ProformaInvoiceScreen1;

